import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Configuration options for Sentry initialization.
 */
export interface SentryConfig {
    /** Sentry DSN (Data Source Name) */
    dsn: string;
    /** Release version (e.g., 'app@1.0.0') */
    release: string;
    /** Distribution/build identifier */
    dist: string;
    /** Environment name (e.g., 'prod', 'test', 'development') */
    environment: string;
    /** Whether Sentry should be enabled */
    enabled: boolean;
}

/**
 * Breadcrumb data for logging user actions leading up to an error.
 */
export interface SentryBreadcrumb {
    category: string;
    message: string;
    level: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
    data?: Record<string, unknown>;
}

/**
 * Service for managing Sentry error tracking and performance monitoring.
 *
 * This service encapsulates all Sentry functionality, providing:
 * - Environment-aware initialization (only initializes when enabled)
 * - User context management
 * - Error capturing
 * - Breadcrumb logging
 *
 * @example
 * ```typescript
 * // In app.component.ts
 * if (environment.enableSentry) {
 *     this.sentryService.initialize({
 *         dsn: environment.sentryDsn,
 *         release: 'app@' + environment.appVersion,
 *         dist: environment.build,
 *         environment: environment.appEnvironment,
 *         enabled: environment.enableSentry
 *     });
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class SentryService {

    private _isInitialized = false;

    /**
     * Whether Sentry has been successfully initialized.
     */
    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * Initializes Sentry with the provided configuration.
     *
     * Will not initialize if:
     * - Already initialized
     * - enabled is false
     * - DSN is empty or missing
     *
     * @param config Sentry configuration options
     */
    public initialize(config: SentryConfig): void {
        // Guard: Don't re-initialize
        if (this._isInitialized) {
            return;
        }

        // Guard: Don't initialize if disabled
        if (!config.enabled) {
            return;
        }

        // Guard: Don't initialize without a valid DSN
        if (!config.dsn || config.dsn.trim() === '') {
            return;
        }

        Sentry.init({
            dsn: config.dsn,
            release: config.release,
            dist: config.dist,
            environment: config.environment,
            enabled: config.enabled,
            integrations: [
                Sentry.captureConsoleIntegration({
                    levels: ['error']
                }),
                Sentry.browserTracingIntegration()
            ],
            tracePropagationTargets: [
                'localhost',
                'api.showreport.nl',
                /^\//  // Relative URLs
            ],
            // Sample 10% of transactions for performance monitoring
            // Higher rates increase costs and may impact privacy
            tracesSampleRate: 0.1,

            // Filter out common non-actionable errors
            beforeSend(event, hint) {
                const error = hint.originalException as Error | undefined;

                // Ignore network errors from user connectivity issues
                if (error?.message?.includes('NetworkError')) {
                    return null;
                }

                // Ignore cancelled/aborted requests
                if (error?.name === 'HttpErrorResponse' &&                    (error as any)?.status === 0) {
                    return null;
                }

                return event;
            }
        });

        this._isInitialized = true;
    }

    /**
     * Sets the user context for error tracking.
     * This helps identify which user experienced an error.
     *
     * @param userId The user's ID (null if not logged in)
     * @param username The user's username (null if unknown)
     */
    public setUserContext(userId: number | null, username: string | null): void {
        const id = userId ?? 0;
        const name = username ?? 'anonymous';

        Sentry.setUser({
            id: id.toString(),
            username: name
        });
    }

    /**
     * Manually captures an exception and sends it to Sentry.
     * Only works if Sentry has been initialized.
     *
     * @param error The error to capture
     * @param context Optional additional context
     */
    public captureException(error: Error, context?: Record<string, unknown>): void {
        if (!this._isInitialized) {
            return;
        }

        if (context) {
            Sentry.withScope(scope => {
                scope.setExtras(context);
                Sentry.captureException(error);
            });
        } else {
            Sentry.captureException(error);
        }
    }

    /**
     * Adds a breadcrumb to help trace the user's path to an error.
     * Only works if Sentry has been initialized.
     *
     * @param breadcrumb The breadcrumb data
     */
    public addBreadcrumb(breadcrumb: SentryBreadcrumb): void {
        if (!this._isInitialized) {
            return;
        }

        Sentry.addBreadcrumb({
            category: breadcrumb.category,
            message: breadcrumb.message,
            level: breadcrumb.level,
            data: breadcrumb.data
        });
    }

    /**
     * Captures a message (non-error) and sends it to Sentry.
     * Useful for tracking important events.
     *
     * @param message The message to capture
     * @param level The severity level
     */
    public captureMessage(
        message: string,
        level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
    ): void {
        if (!this._isInitialized) {
            return;
        }

        Sentry.captureMessage(message, level);
    }
}
